import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { isEqual } from 'lodash';
import findResult from '@/utils/resultsHelper';
import { clientError, firstError, firstResponseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import { usersHavePermissions } from '@/users/helper';
import MemberPermissionsTable from '@/users/membership/components/MemberPermissionsTable';
import Dialog from '@/components/dialogs/Dialog';
import { searchMembers, searchPerms } from '../state/state';
import {
  hideShareSearch,
  fetchSearchMembers,
  removeSearchMember,
  updateSearchMemberPermissions,
  shareSearch,
  updateShareSearchProperties,
} from '../actions';
import SearchMemberAddRow from './SearchMemberAddRow';
import { searchPermissions } from '../../constants';
import { searchCreatorPermissions, searchPermissionGroups } from '../constants';

const ShareSearchDialog = ({
  allowAdd,
  allowRemove,
  allowSecure,
  category,
  code,
  fetchData,
  hide,
  isDirty,
  members,
  name,
  readStatus,
  removeMember,
  share,
  theme,
  updateErrors,
  updatePermissions,
  updateProperties,
  updateStatus,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => updateProperties({ updateErrors: newErrors });

  const titleText = () => ((allowAdd || allowRemove)
    ? intl.formatMessage({ id: 'search.share.title' }, { name })
    : intl.formatMessage({ id: 'search.security.title' }, { name }));

  const titleIcon = () => ((allowAdd || allowRemove) ? 'share alternate' : 'shield');

  const handleClose = () => {
    hide(category, code);
  };

  const validateForm = () => {
    const validationErrors = [];

    if (!usersHavePermissions(members, searchCreatorPermissions)) {
      validationErrors.push(
        clientError(
          { entity: 'search' },
          intl.formatMessage({ id: 'share.search.validation.no.owners.title' }),
          intl.formatMessage({ id: 'share.search.validation.owners.required' }),
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const handleDismiss = () => {
    setErrors([]);
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      share();
    }
  };

  return (
    <Dialog
      className="aridhia-search-share-dialog smallwizard"
      error={firstResponseError(updateErrors)}
      icon={titleIcon()}
      inProgress={updateStatus === states.PENDING}
      minimumDataEntryMet={isDirty}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId="actions.apply"
      size="large"
      theme={theme}
      title={titleText()}
    >
      <div className="aridhia-user-permissions-dialog-container">
        <MemberPermissionsTable
          allowAdd={allowAdd}
          allowRemove={allowRemove}
          allowSecure={allowSecure}
          entity="search"
          error={firstError(updateErrors)}
          fetchData={() => fetchData(category, code)}
          members={members}
          onErrorDismiss={handleDismiss}
          permissionGroups={searchPermissionGroups}
          readStatus={readStatus}
          removeMember={(id) => removeMember(category, code, id)}
          updatePermissions={(id, value) => updatePermissions(category, code, id, value)}
        >
          {allowAdd && (<SearchMemberAddRow category={category} code={code} allowSecure={allowSecure} />)}
        </MemberPermissionsTable>
      </div>
    </Dialog>
  );
};

const mapStateToProps = ({
  auth: { role: { permissions: { search: { share, secure } } } },
  search: {
    sharingSearch, searches,
  },
}) => {
  const { members, name, permissions } = findResult(searches, sharingSearch.category, sharingSearch.code) || {
    members: searchMembers(),
    name: '',
    permissions: searchPerms(),
  };
  const {
    results, original, readStatus, readErrors,
  } = members;

  return (
    {
      ...sharingSearch,
      allowAdd: share && permissions[searchPermissions.INVITE_MEMBER],
      allowRemove: share && permissions[searchPermissions.REMOVE_MEMBER],
      allowSecure: secure && permissions[searchPermissions.SECURITY],
      isDirty: !isEqual(results, original),
      members: results,
      name,
      readErrors,
      readStatus,
    }
  );
};

const mapDispatchToProps = {
  share: shareSearch,
  hide: hideShareSearch,
  fetchData: fetchSearchMembers,
  removeMember: removeSearchMember,
  updateProperties: updateShareSearchProperties,
  updatePermissions: updateSearchMemberPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareSearchDialog);
